"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger
} from "@nextui-org/react";
import {useMemo, useState} from "react";
import {
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiMapPin,
  FiUser
} from "react-icons/fi";
import {PiBusLight} from "react-icons/pi";

type SosCurrentTab = "active" | "resolved" | "falseAlarm";

type SosUser = {
  id: string;
  name: string;
  bookingId: string;
  avatar: string;
  phone: string;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  totalTravelTime: string;
};

type SosCardItem = {
  id: string;
  busNo: string;
  pickupTime: string;
  caretakerName: string;
  driverCode: string;
  driverPh: string;
  from: string;
  to: string;
  status: 1 | 2 | 3;
  users: SosUser[];
};

const getActionButtonConfig = (tab: SosCurrentTab) => {
  switch (tab) {
    case "resolved":
      return {
        label: "Resolved",
        className:
          "h-9 min-w-[132px] rounded-xl bg-green-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "cancelled", label: "Cancelled"}]
      };
    case "falseAlarm":
      return {
        label: "Cancelled",
        className:
          "h-9 min-w-[132px] rounded-xl bg-black px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "resolved", label: "Resolved"}]
      };
    default:
      return {
        label: "Take Action",
        className:
          "h-9 min-w-[132px] rounded-xl bg-rose-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [
          {key: "resolved", label: "Resolved"},
          {key: "cancelled", label: "Cancelled"}
        ]
      };
  }
};

export const SOSCard = ({
  b,
  currentTab = "active",
  onStatusChange
}: {
  b: SosCardItem;
  statusDetails?: any;
  onStatusChange: (
    cardId: string,
    userId: string,
    nextStatus: 1 | 2 | 3
  ) => void;
  currentTab?: SosCurrentTab;
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const actionButton = getActionButtonConfig(currentTab);
  const firstUser = b.users[0];
  const routeName = `Route ${Number(b.users[0]?.bookingId ?? 0) % 12 || 4}`;
  const affectedUsers = b.users.length;
  const visibleUsers = useMemo(() => b.users, [b.users]);

  return (
    <>
      <div className="h-64 w-full min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="truncate font-bold leading-none tracking-tight text-slate-900">
                {b.busNo}
              </div>
              <div className="mt-1 truncate text-xs font-medium text-slate-500">
                {routeName}
              </div>
            </div>

            <div className="w-32 shrink-0">
              <div className="mt-2 text-right text-xs font-medium text-slate-500">
                <span className="font-semibold text-slate-700">
                  {affectedUsers}
                </span>{" "}
                Users Affected
              </div>
              <div className="mt-1.5 flex justify-end"></div>
            </div>
          </div>

          <div className="rounded-[18px] bg-slate-50 px-4 py-1 text-sm font-semibold text-slate-900">
            {firstUser
              ? `${firstUser.travelFrom} - ${firstUser.travelTo}`
              : "-"}
          </div>

          <div className="relative mt-1 space-y-2 pl-9">
            <div className="absolute left-[11px] top-5 h-[30px] w-0.5 border-l-2 border-dotted border-slate-200" />
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
                <FiMapPin className="h-3 w-3 text-blue-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.from}
              </div>
            </div>
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50">
                <FiMapPin className="h-3 w-3 text-emerald-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.to}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <FiCalendar className="h-3.5 w-3.5 text-slate-500" />
                <span>{firstUser?.travelDate ?? "-"}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiUser className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.caretakerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <PiBusLight className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.driverCode}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              className="flex w-36 items-center justify-center rounded-[18px] border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
              onClick={() => setIsDetailOpen(true)}
              type="button"
            >
              Detail View
            </button>
          </div>
        </div>
      </div>

      <NeoModal
        actionType="sosDetailView"
        onOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        hasActions={false}
        data={{
          card: b,
          currentTab,
          onStatusChange
        }}
      />
    </>
  );
};
